<script>
import Layout from "../../../layouts/main";
import appConfig from "../../../../../app.config";
import newScoresAPI from "../../../../services/newScores/newScoresAPI";
import { normalize, CYCLES, fmtNum, fmtPct, semaphore } from "@/data/dashboardModel";
import PhvaWheel from "@/components/dash/PhvaWheel";
import NextActions from "@/components/dash/NextActions";
import BulletBar from "@/components/dash/BulletBar";
import TrendArea from "@/components/dash/TrendArea";
import ItemMosaic from "@/components/dash/ItemMosaic";
import Meter from "@/components/dash/Meter";
import StatusMark from "@/components/dash/StatusMark";
import DashEmpty from "@/components/dash/DashEmpty";
import DashTable from "@/components/dash/DashTable";
import DashSkeleton from "@/components/dash/DashSkeleton";

/**
 * Dashboard "Brújula de cumplimiento" — spec: docs/sdd/02-spec-dashboard.md
 * Pregunta: ¿cuánto del SG-SST cumple hoy la Res. 0312/2019 y qué ítem apruebo a continuación?
 */
export default {
  page: {
    title: "Inicio",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    PhvaWheel,
    NextActions,
    BulletBar,
    TrendArea,
    ItemMosaic,
    Meter,
    StatusMark,
    DashEmpty,
    DashTable,
    DashSkeleton,
  },
  data() {
    return {
      loading: true,
      error: false,
      raw: null,
      ready: false, // dispara la animación de entrada de arcos/barras
      updatedAt: null,
      tables: { trend: false, mosaic: false },
      currentDate: new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }),
    };
  },
  computed: {
    breakdownErrorText() {
      const code = this.model.breakdownErrorId ? ` Código para soporte: ${this.model.breakdownErrorId}.` : "";
      return `Hubo un problema al armar este bloque en el servidor. Actualiza en unos minutos; si sigue, avisa a soporte.${code}`;
    },
    company() {
      return this.$store.getters["company/getCompany"] || {};
    },
    companyId() {
      return this.company.id || null;
    },
    model() {
      return this.raw ? normalize(this.raw) : null;
    },
    companyName() {
      return (this.model && this.model.company) || this.company.social_reason || this.company.name || "";
    },
    estandaresRoute() {
      return "/forms/estandares";
    },
    classifyRoute() {
      return this.companyId ? `/forms/editar-empresa/${this.companyId}` : "/forms/listado-empresa";
    },
    updatedText() {
      if (!this.updatedAt) return "";
      return `Actualizado ${this.updatedAt.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}`;
    },
    // --- B1 ---
    scopeLabel() {
      const m = this.model;
      return m ? `Base: ${m.total.requiredItems} ítems requeridos de ${m.classificationShort}.` : "";
    },
    // --- B2 ---
    pendingCount() {
      return this.model ? this.model.items.filter((i) => i.status !== "Aprobado").length : 0;
    },
    nextGain() {
      const m = this.model;
      if (!m || !m.next.length || !m.total.requiredScore) return null;
      const sum = Math.round(m.next.reduce((s, i) => s + i.value, 0) * 100) / 100;
      const to = ((m.total.approvedScore + sum) / m.total.requiredScore) * 100;
      return { sum, to };
    },
    // --- B3 ---
    standardRows() {
      if (!this.model) return [];
      return this.model.standards.map((s) => {
        const c = CYCLES.find((x) => x.key === s.cycle);
        return { ...s, cycleLabel: c ? c.label : "" };
      });
    },
    weakestStandard() {
      const rows = this.standardRows.filter((r) => r.pct !== null);
      if (!rows.length) return null;
      return rows.reduce((a, b) => (b.pct < a.pct ? b : a));
    },
    // --- B4 / B5 tablas alternativas ---
    trendRows() {
      if (!this.model) return [];
      return this.model.history.map((h) => ({
        month: h.longLabel,
        pct: fmtPct(h.pct),
        pts: fmtNum(h.approvedScore),
        status: semaphore(h.pct).label,
      }));
    },
    mosaicRows() {
      if (!this.model) return [];
      return this.model.items.map((i) => ({
        seq: i.sequence,
        desc: i.description,
        cycle: (CYCLES.find((c) => c.key === i.cycle) || {}).label || "",
        pts: fmtNum(i.value),
        status: i.status,
      }));
    },
    // --- B6 ---
    accidentSub() {
      const a = this.model.accident;
      return {
        freq: a.frequencyIndex != null ? `Índice de frecuencia ${fmtNum(a.frequencyIndex)}` : "",
        sev: a.severityIndex != null ? `Índice de severidad ${fmtNum(a.severityIndex)}` : "",
      };
    },
    // --- columna derecha de la fila 3 ---
    hasSide() {
      return !!this.model && (this.model.accident.show || this.model.complementary.show);
    },
  },
  methods: {
    fmtNum,
    fmtPct,
    getData() {
      if (!this.companyId) {
        this.loading = false;
        return;
      }
      let token = null;
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        token = user && user.token;
      } catch (e) {
        token = null;
      }
      this.loading = true;
      this.error = false;
      newScoresAPI
        .scores({ access_token: token, companies_id: this.companyId })
        .then((response) => {
          // http.js devuelve undefined/null ante fallo de red o cuerpo vacío
          if (!response || typeof response.data !== "object" || response.data === null || Array.isArray(response.data)) {
            throw new Error("Respuesta sin datos");
          }
          this.raw = response.data;
          this.updatedAt = new Date();
          this.loading = false;
          this.ready = false;
          this.$nextTick(() => {
            // un frame con el estado inicial pintado para que la transición corra
            requestAnimationFrame(() => requestAnimationFrame(() => (this.ready = true)));
          });
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.log("[dashboard]", e && e.message);
          this.error = true;
          this.loading = false;
        });
    },
  },
  mounted() {
    this.getData();
  },
};
</script>

<template>
  <Layout>
    <div class="dsh">
      <!-- B0 · título, hallazgo, empresa/grupo/fecha, acción principal -->
      <header class="dsh-head">
        <div class="dsh-head__text">
          <h1 class="dsh-head__title">Brújula de cumplimiento</h1>
          <p v-if="model && model.classified" class="dsh-head__finding">{{ model.finding }}</p>
          <p v-else-if="loading && companyId" class="dsh-head__finding dsh-head__finding--skel" aria-hidden="true"></p>
          <p v-else-if="!companyId" class="dsh-head__finding">Elige una empresa para ver cuánto cumple de la Res. 0312/2019.</p>
          <p class="dsh-head__meta">
            <span v-if="companyName">{{ companyName }}</span>
            <span v-if="model">{{ model.classificationLabel }}</span>
            <span>{{ currentDate }}</span>
          </p>
        </div>
        <div class="dsh-head__actions">
          <router-link :to="estandaresRoute" class="dsh-btn dsh-btn--primary">Ir a estándares</router-link>
          <button v-if="companyId" type="button" class="dsh-btn dsh-btn--text" :disabled="loading" @click="getData">
            {{ loading ? "Actualizando…" : "Actualizar" }}
          </button>
          <span v-if="updatedText && !loading" class="dsh-head__stamp">{{ updatedText }}</span>
        </div>
      </header>

      <!-- Estado: sin empresa -->
      <section v-if="!companyId" class="dsh-panel dsh-state" aria-labelledby="st-company">
        <h2 id="st-company" class="dsh-panel__title">Aún no elegiste una empresa</h2>
        <p class="dsh-state__text">
          El cumplimiento se mide por empresa. Elige una en el selector de la barra superior o ve al listado de empresas.
        </p>
        <router-link to="/forms/listado-empresa" class="dsh-btn dsh-btn--primary">Ver empresas</router-link>
      </section>

      <!-- Estado: cargando -->
      <DashSkeleton v-else-if="loading && !model" />

      <!-- Estado: error de red -->
      <section v-else-if="error && !model" class="dsh-panel dsh-state" role="alert" aria-labelledby="st-error">
        <h2 id="st-error" class="dsh-panel__title">No pudimos cargar el cumplimiento</h2>
        <p class="dsh-state__text">El servidor no respondió con datos. Tus ítems no cambiaron; revisa tu conexión e inténtalo de nuevo.</p>
        <button type="button" class="dsh-btn dsh-btn--primary" @click="getData">Reintentar</button>
      </section>

      <!-- Estado: sin clasificar (ningún 0 % engañoso) -->
      <section v-else-if="model && !model.classified" class="dsh-panel dsh-state" aria-labelledby="st-class">
        <h2 id="st-class" class="dsh-panel__title">Esta empresa aún no está clasificada</h2>
        <p class="dsh-state__text">
          La Res. 0312/2019 fija los estándares mínimos según el número de trabajadores y el nivel de riesgo (UPA, Grupo 1, 2 o 3).
          Sin esa clasificación no hay una norma contra la cual medir, así que no mostramos un porcentaje.
        </p>
        <router-link :to="classifyRoute" class="dsh-btn dsh-btn--primary">Clasificar la empresa</router-link>
      </section>

      <!-- Dashboard -->
      <div v-else-if="model" class="dsh-body" :class="{ 'is-stale': loading }">
        <p v-if="error" class="dsh-banner" role="alert">
          No pudimos actualizar los datos. Ves los últimos cargados{{ updatedText ? " (" + updatedText.toLowerCase() + ")" : "" }}.
          <button type="button" class="dsh-btn dsh-btn--text" @click="getData">Reintentar</button>
        </p>

        <div class="dsh-grid">
          <!-- B1 · rueda PHVA -->
          <section class="dsh-panel dsh-b1" aria-labelledby="b1-t">
            <header class="dsh-panel__head">
              <h2 id="b1-t" class="dsh-panel__title">Ciclo PHVA</h2>
              <p class="dsh-panel__base">
                Puntaje ponderado de los {{ model.total.requiredItems }} ítems requeridos · {{ model.classificationShort }}, Res. 0312/2019
              </p>
            </header>
            <PhvaWheel :phva="model.phva" :total="model.total" :ready="ready" :scope-label="scopeLabel" :to="estandaresRoute" />
          </section>

          <!-- B2 · siguientes acciones -->
          <section class="dsh-panel dsh-b2" aria-labelledby="b2-t">
            <header class="dsh-panel__head">
              <h2 id="b2-t" class="dsh-panel__title">Siguientes acciones</h2>
              <p v-if="model.next.length && nextGain" class="dsh-panel__base">
                Los {{ model.next.length }} ítems pendientes de mayor peso. Aprobarlos suma {{ fmtNum(nextGain.sum) }} puntos:
                pasarías de {{ fmtPct(model.total.pct) }} a {{ fmtPct(nextGain.to) }}.
              </p>
              <p v-else-if="model.next.length" class="dsh-panel__base">Los {{ model.next.length }} ítems pendientes de mayor peso, en puntos.</p>
            </header>
            <NextActions v-if="model.next.length" :items="model.next" :to="estandaresRoute" />
            <DashEmpty
              v-else-if="model.hasItems"
              title="No tienes ítems pendientes"
              :text="`Los ${model.statusCounts.Aprobado} ítems requeridos de ${model.classificationShort} están aprobados.`"
            />
            <DashEmpty
              v-else
              :title="model.breakdownError ? 'No pudimos calcular el detalle' : 'Aún no hay detalle de ítems'"
              :text="model.breakdownError ? breakdownErrorText : 'Verás aquí los ítems por aprobar, del que más puntos suma al que menos, cuando el servidor entregue el detalle.'"
            />
            <footer v-if="pendingCount > model.next.length" class="dsh-panel__foot">
              <router-link :to="estandaresRoute" class="dsh-link">Ver los {{ pendingCount }} pendientes en Estándares</router-link>
            </footer>
          </section>

          <!-- B3 · cumplimiento por estándar -->
          <section class="dsh-panel dsh-b3" aria-labelledby="b3-t">
            <header class="dsh-panel__head">
              <h2 id="b3-t" class="dsh-panel__title">Cumplimiento por estándar</h2>
              <p class="dsh-panel__base">
                Puntaje ponderado de cada estándar. Las marcas señalan 60 % y 85 %, los umbrales de la Res. 0312/2019.
                <template v-if="weakestStandard && weakestStandard.pct < 100">
                  El más flojo: {{ weakestStandard.number }}. {{ weakestStandard.name }} ({{ fmtPct(weakestStandard.pct, 1) }}).
                </template>
              </p>
            </header>
            <BulletBar v-if="model.hasStandards" :rows="standardRows" :ready="ready" :to="estandaresRoute" />
            <DashEmpty
              v-else
              :title="model.breakdownError ? 'No pudimos calcular el detalle' : 'Aún no hay desglose por estándar'"
              :text="model.breakdownError ? breakdownErrorText : 'Aparecerá cada estándar con su porcentaje y sus ítems cuando el servidor entregue el detalle.'"
              :min-height="180"
            />
          </section>

          <!-- B4 · evolución -->
          <section class="dsh-panel dsh-b4" aria-labelledby="b4-t">
            <header class="dsh-panel__head dsh-panel__head--row">
              <div>
                <h2 id="b4-t" class="dsh-panel__title">Evolución del avance</h2>
                <p class="dsh-panel__base">
                  Puntaje ponderado acumulado al cierre de cada mes, últimos {{ model.history.length || 12 }} meses.
                  <template v-if="model.trend">
                    <strong>{{ model.trend.delta > 0 ? "+" : "" }}{{ fmtNum(model.trend.delta) }} puntos porcentuales</strong>
                    desde {{ model.trend.from }}.
                  </template>
                </p>
              </div>
              <button
                v-if="model.hasHistory"
                type="button"
                class="dsh-btn dsh-btn--text"
                :aria-pressed="String(tables.trend)"
                @click="tables.trend = !tables.trend"
              >Ver como tabla</button>
            </header>
            <template v-if="model.hasHistory">
              <DashTable
                v-if="tables.trend"
                caption="Puntaje ponderado acumulado por mes"
                :columns="[
                  { key: 'month', label: 'Mes' },
                  { key: 'pct', label: '% ponderado', align: 'right' },
                  { key: 'pts', label: 'Puntos aprobados', align: 'right' },
                  { key: 'status', label: 'Valoración' },
                ]"
                :rows="trendRows"
              />
              <TrendArea v-else :history="model.history" :ready="ready" :required-score="model.total.requiredScore" />
            </template>
            <DashEmpty
              v-else
              :title="model.breakdownError ? 'No pudimos calcular el detalle' : 'Aún no hay historial'"
              :text="model.breakdownError ? breakdownErrorText : 'Cuando se acumulen cierres de mes verás aquí si el cumplimiento sube o baja.'"
              :min-height="200"
            />
          </section>

          <!-- B5 · mosaico de ítems -->
          <section class="dsh-panel dsh-b5" :class="{ 'dsh-b5--wide': !hasSide }" aria-labelledby="b5-t">
            <header class="dsh-panel__head dsh-panel__head--row">
              <div>
                <h2 id="b5-t" class="dsh-panel__title">Mosaico de ítems</h2>
                <p class="dsh-panel__base">
                  {{ model.total.requiredItems }} ítems requeridos por la Res. 0312/2019 para {{ model.classificationShort }}.
                  El tamaño de cada celda es su peso en puntos; haz clic para ir a Estándares.
                </p>
              </div>
              <button
                v-if="model.hasItems"
                type="button"
                class="dsh-btn dsh-btn--text"
                :aria-pressed="String(tables.mosaic)"
                @click="tables.mosaic = !tables.mosaic"
              >Ver como tabla</button>
            </header>
            <template v-if="model.hasItems">
              <DashTable
                v-if="tables.mosaic"
                class="dsh-tablewrap--tall"
                caption="Ítems requeridos con su ciclo, peso y estado"
                :columns="[
                  { key: 'seq', label: 'Ítem' },
                  { key: 'desc', label: 'Descripción' },
                  { key: 'cycle', label: 'Ciclo' },
                  { key: 'pts', label: 'Peso (pts)', align: 'right' },
                  { key: 'status', label: 'Estado' },
                ]"
                :rows="mosaicRows"
              />
              <ItemMosaic v-else :items="model.items" :ready="ready" :to="estandaresRoute" />
            </template>
            <DashEmpty
              v-else
              :title="model.breakdownError ? 'No pudimos calcular el detalle' : 'Aún no hay detalle de ítems'"
              :text="model.breakdownError ? breakdownErrorText : 'Verás cada ítem de la norma como una celda, coloreada por su estado, cuando el servidor entregue el detalle.'"
              :min-height="220"
            />
          </section>

          <!-- B6 · accidentalidad (solo GRUPO2 / GRUPO3 con datos) + B7 · complementarios -->
          <div v-if="hasSide" class="dsh-side">
            <section v-show="model.accident.show" class="dsh-panel dsh-b6" aria-labelledby="b6-t">
              <header class="dsh-panel__head">
                <h2 id="b6-t" class="dsh-panel__title">Accidentalidad {{ model.accident.year }}</h2>
                <p class="dsh-panel__base">Lo ocurrido en el año frente a la meta anual de la empresa, que es un techo.</p>
              </header>
              <Meter
                title="Frecuencia"
                unit="accidentes"
                unit-one="accidente"
                :value="model.accident.count"
                :goal="model.accident.countGoal"
                :index="accidentSub.freq"
                :ready="ready"
                :to="estandaresRoute"
              />
              <Meter
                title="Severidad"
                unit="días"
                unit-one="día"
                :value="model.accident.days"
                :goal="model.accident.daysGoal"
                :index="accidentSub.sev"
                :ready="ready"
                :to="estandaresRoute"
              />
            </section>

            <section v-if="model.complementary.show" class="dsh-panel dsh-b7" aria-labelledby="b7-t">
              <h2 id="b7-t" class="dsh-panel__title">Ítems complementarios</h2>
              <p class="dsh-panel__base">Los que la norma no le exige a {{ model.classificationShort }} y suman a tu gestión.</p>
              <div class="dsh-thin">
                <span class="dsh-thin__text">
                  <strong>{{ model.complementary.approved }}</strong> de {{ model.complementary.total }} aprobados
                  · {{ fmtPct(model.complementary.pct, 1) }}
                </span>
                <span
                  class="dsh-thin__track"
                  role="img"
                  :aria-label="`${model.complementary.approved} de ${model.complementary.total} ítems complementarios aprobados`"
                >
                  <span class="dsh-thin__fill" :style="{ width: (ready ? model.complementary.pct : 0) + '%' }"></span>
                </span>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
